import {inject, Injectable} from '@angular/core';
import {FirebaseApp} from "@angular/fire/app";
import type {Analytics} from "@angular/fire/analytics";
import {trackConsentChange, Tracker} from "./tracking";
import {TrackingEvent, TrackingEventType} from "./tracking-event";
import {ConsentService, CookieYesCategory} from "../services/consent.service";

type AnalyticsSdk = typeof import("@angular/fire/analytics");

/**
 * Lazily loads Firebase (Google) Analytics. Calls made before {@link enable} (or after {@link disable}) are no-ops.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements Tracker {

  private readonly firebaseApp = inject(FirebaseApp);
  private sdk?: Promise<{ sdk: AnalyticsSdk, analytics: Analytics } | undefined>;
  private enabled = false;

  constructor() {
    const consentType = inject(ConsentService).signalFor(this.category());
    trackConsentChange(this, consentType);
  }

  category(): CookieYesCategory {
    return 'analytics';
  }

  public enable(): void {
    if (this.enabled) {
      return;
    }
    this.enabled = true;
    this.sdk ??= this.load();
    this.withSdk(({sdk, analytics}) => {
      sdk.setConsent({analytics_storage: 'granted'});
      sdk.setAnalyticsCollectionEnabled(analytics, true);
    });
  }

  public disable(): void {
    if (!this.enabled) {
      return;
    }
    this.enabled = false;
    this.withSdk(({sdk, analytics}) => {
      sdk.setConsent({analytics_storage: 'denied'});
      sdk.setAnalyticsCollectionEnabled(analytics, false);
    });
  }

  public trackPageView(path: string): void {
    if (!this.enabled) {
      return;
    }
    this.withSdk(({sdk, analytics}) => sdk.logEvent(analytics, 'page_view', {
      page_path: path,
      page_location: location.href
    }));
  }

  public trackCustomEvent(event: TrackingEvent) {
    if (!this.enabled) {
      return;
    }
    this.withSdk(({sdk, analytics}) => sdk.logEvent(
      analytics,
      ANALYTICS_EVENT_MAPPING[event.type],
      event.data
    ));
  }

  private withSdk(action: (loaded: { sdk: AnalyticsSdk, analytics: Analytics }) => void): void {
    this.sdk?.then(loaded => loaded && action(loaded));
  }

  private async load() {
    const sdk = await import("@angular/fire/analytics");
    if (!await sdk.isSupported()) {
      return undefined;
    }
    // must be set before initialization, advertising features stay disabled
    sdk.setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
    // page views are tracked manually, only for routes with tracking enabled
    const analytics = sdk.initializeAnalytics(this.firebaseApp, {config: {send_page_view: false}});
    return {sdk, analytics};
  }

}

const ANALYTICS_EVENT_MAPPING: Record<TrackingEventType, string> = {
  [TrackingEventType.SCHEDULE_INITIATED]: 'schedule_initiated',
  [TrackingEventType.SCHEDULED]: 'scheduled',
  [TrackingEventType.NAVIGATION_CHANGED]: 'navigation_changed',
  [TrackingEventType.BLOG_SEARCHED]: 'blog_searched'
};
